import { createSlice } from "@reduxjs/toolkit";
import { StoreDispatch } from "../store";
import swal from "sweetalert2";
import { User, Gender, Initial, kycStatusType } from "src/types/commonTypes";
import {
  getUserById,
  updateUser,
  getConnectedUserWallets,
  UpdateUserRequest,
  updateUserRole,
  UpdateUserRoleRequest,
  searchUser,
  saveUserPreferences,
  SearchUserRequest,
  SaveUserPreferencesInput,
  getUserPreferencesForSave,
  saveUserMfaExemption,
  resetUserMfa
} from "src/network/graphql/userService";
import { Transaction } from "../transactions/transactionSlice";
import {
  SearchWalletRequest,
  connectUserToWallet,
  removeUserFromWallet,
  searchWallet
} from "src/network/graphql/walletService";
import { Wallet } from "../wallets/walletSlice";
import { immediateToast } from "izitoast-react";
import { TransactionRequest, getAllTransaction } from "src/network/graphql/transactionService";
import { resetPassword } from "src/network/graphql/configService";
import { Card } from "../cards/cardSlice";
import { CardSearchRequest, cardSearch } from "src/network/graphql/cardService";
import { Users } from "../users/usersSlice";
import { EMPTY_STRING } from "src/components/constant/constant";
import { formatFullName } from "src/utils";
import { getGroupList, getUserGroup, SearchGroupListRequest } from "src/network/graphql/groupService";
import { initialGroup } from "../group/groupSlice";

export type UserDetail = {
  user: {
    id: number;
    cognitoUserName: string;
    displayId: string;
    active: boolean;
    email: string;
    userName: string;
    initial: Initial | null;
    firstName: string;
    lastName: string;
    dob: string;
    phoneNumber: string;
    gender: Gender | null;
    role: {
      organisation_id: string;
      value: string;
    }[];
    state: string;
    city: string;
    line1: string;
    line2: string;
    zipcode: string;
    country: string;
    accessCode: string;
    fullName: string;
    guardianId: number;
    kycStatus: kycStatusType | null;
    profileImage: string;
    preferences: {
      ndis_number: string;
      licence_tag: string;
      exclude_license: boolean;
      mfa_exempt?: boolean;
    };
  };
  guardian: User | null;
  canDoActiveInactive: boolean;
  canEditRole: boolean;
  canEditGuardian: boolean;
  canEditKycStatus: boolean;
};

export const initialUserDetail: UserDetail = {
  user: {
    id: 0,
    cognitoUserName: EMPTY_STRING,
    displayId: EMPTY_STRING,
    active: false,
    email: EMPTY_STRING,
    userName: EMPTY_STRING,
    initial: "Mr.",
    firstName: EMPTY_STRING,
    lastName: EMPTY_STRING,
    dob: EMPTY_STRING,
    phoneNumber: EMPTY_STRING,
    gender: "male",
    role: [],
    state: EMPTY_STRING,
    city: EMPTY_STRING,
    line1: EMPTY_STRING,
    line2: EMPTY_STRING,
    zipcode: EMPTY_STRING,
    country: EMPTY_STRING,
    accessCode: EMPTY_STRING,
    fullName: EMPTY_STRING,
    guardianId: 0,
    kycStatus: "Fail",
    profileImage: EMPTY_STRING,
    preferences: {
      ndis_number: EMPTY_STRING,
      licence_tag: EMPTY_STRING,
      exclude_license: false
    }
  },
  guardian: null,
  canDoActiveInactive: false,
  canEditRole: false,
  canEditGuardian: false,
  canEditKycStatus: false
};

type GuardianDetail = {
  user: {
    id: number;
    fullName: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
  };
};

const initialGuardianDetail: GuardianDetail = {
  user: {
    id: 0,
    fullName: EMPTY_STRING,
    firstName: EMPTY_STRING,
    lastName: EMPTY_STRING,
    email: EMPTY_STRING,
    role: EMPTY_STRING
  }
};

const initialWallets: Wallet[] = [];

const initialTransaction: Transaction[] = [];

type walletTransaction = {
  id: number;
  current: number;
  previous: number;
  positive: boolean;
  diff: number;
};

const initialwalletTransaction: walletTransaction[] = [];
const initialCards: Card[] = [];
const initialUsers: Users[] = [];

const userDetailSlice = createSlice({
  name: "userDetail",
  initialState: {
    userDetailLoading: false,
    walletLoading: false,
    walletListLoading: false,
    transLoading: false,
    walletTransLoading: false,
    userLoading: false,
    guardianDetailloading: false,
    resetPasswordLoading: false,
    participantLoading: false,
    participantWalletLoading: false,
    groupsLoading: false,
    userGroupsLoading: false,
    loading: false,
    error: null,
    userDetail: initialUserDetail,
    WalletConnect: {
      wallets: initialWallets,
      total: 0
    },
    walletList: {
      list: initialWallets,
      total: 0
    },
    transList: {
      trans: initialTransaction,
      total: 0
    },
    walletTransaction: initialwalletTransaction,
    guardianDetail: initialGuardianDetail,
    cards: {
      list: initialCards,
      total: 0
    },
    participants: {
      list: initialUsers,
      total: 0
    },
    participantWallet: {
      wallets: initialWallets,
      total: 0
    },
    userGroups: {
      list: initialGroup,
      total: 0
    },
    groups: {
      list: initialGroup,
      total: 0
    }
  },
  reducers: {
    fetchUserDetailStart: (state) => {
      state.userDetailLoading = true;
    },
    fetchUserDetailSuccess: (state, action) => {
      state.userDetailLoading = false;
      state.userDetail = action.payload ?? initialUserDetail;
    },
    setUserMfaExempt: (state, action) => {
      state.userDetail.user.preferences.mfa_exempt = action.payload;
    },
    fetchUserDetailFail: (state, action) => {
      state.userDetailLoading = false;
      state.userDetail = initialUserDetail;
      state.error = action.payload;
    },
    fetchWalletsStart: (state) => {
      state.walletLoading = true;
    },
    fetchWalletsSuccess: (state, action) => {
      state.walletLoading = false;
      const list = action.payload?.list ?? [];

      state.WalletConnect.wallets = list;
      state.WalletConnect.total = action.payload?.total ?? list.length;
    },
    fetchWalletsFail: (state, action) => {
      state.walletLoading = false;
      state.WalletConnect.wallets = [];
      state.WalletConnect.total = 0;
      state.error = action.payload;
    },
    fetchWalletListStart: (state) => {
      state.walletListLoading = true;
    },
    fetchWalletListSuccess: (state, action) => {
      state.walletListLoading = false;
      state.walletList.list = action.payload?.list ?? [];
      state.walletList.total = action.payload?.total ?? 0;
    },
    fetchWalletListFail: (state, action) => {
      state.walletListLoading = false;
      state.walletList.list = [];
      state.walletList.total = 0;
      state.error = action.payload;
    },
    fetchTransListStart: (state) => {
      state.transLoading = true;
    },
    fetchTransListSuccess: (state, action) => {
      state.transLoading = false;
      state.transList.trans = action.payload?.list ?? [];
      state.transList.total = action.payload?.total ?? 0;
    },
    fetchTransListFail: (state, action) => {
      state.transLoading = false;
      state.transList.trans = [];
      state.transList.total = 0;
      state.error = action.payload;
    },
    fetchWalletTransactionsStart: (state) => {
      state.walletTransLoading = true;
    },
    fetchWalletTransactionsSuccess: (state, action) => {
      state.walletTransLoading = false;
      state.walletTransaction = action?.payload ?? [];
    },
    fetchWalletTransactionsFail: (state, action) => {
      state.walletTransLoading = false;
      state.walletTransaction = [];
      state.error = action.payload;
    },
    updateUserDetailStart: (state) => {
      state.userDetailLoading = true;
    },
    updateUserDetailSuccess: (state) => {
      state.userDetailLoading = false;
    },
    updateUserDetailFail: (state, action) => {
      state.userDetailLoading = false;
      state.error = action.payload;
    },
    updateUserRoleStart: (state) => {
      state.userDetailLoading = true;
    },
    updateUserRoleSuccess: (state) => {
      state.userDetailLoading = false;
    },
    updateUserRoleFail: (state, action) => {
      state.userDetailLoading = false;
      state.error = action.payload;
    },
    updateUserNdisStart: (state) => {
      state.userDetailLoading = true;
    },
    updateUserNdisSuccess: (state) => {
      state.userDetailLoading = false;
    },
    updateUserNdisFail: (state, action) => {
      state.userDetailLoading = false;
      state.error = action.payload;
    },
    addSupporterStart: (state) => {
      state.walletLoading = true;
    },
    addSupporterSuccess: (state) => {
      state.walletLoading = false;
      state.error = null;
    },
    addSupporterFail: (state, action) => {
      state.walletLoading = false;
      state.error = action.payload;
    },
    deleteSupporterStart: (state) => {
      state.walletLoading = true;
    },
    deleteSupporterSuccess: (state) => {
      state.walletLoading = false;
      state.error = null;
    },
    deleteSupporterFail: (state, action) => {
      state.walletLoading = false;
      state.error = action.payload;
    },
    resetPasswordStart: (state) => {
      state.resetPasswordLoading = true;
    },
    resetPasswordSuccess: (state) => {
      state.resetPasswordLoading = false;
    },
    resetPasswordFail: (state, action) => {
      state.resetPasswordLoading = false;
      state.error = action.payload;
    },
    getAllCardsStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    getAllCardsFail: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    getAllCardsSuccess: (state, action) => {
      state.loading = false;
      state.error = null;
      state.cards = action.payload;
    },
    fetchParticpantStart: (state) => {
      state.participantLoading = true;
    },
    fetchParticpantSuccess: (state, action) => {
      state.participantLoading = false;
      state.participants.list = action.payload?.list ?? [];
      state.participants.total = action.payload?.total ?? 0;
    },
    fetchParticpantFail: (state, action) => {
      state.participantLoading = false;
      state.participants.list = [];
      state.participants.total = 0;
      state.error = action.payload;
    },
    fetchParticipantWalletStart: (state) => {
      state.participantWalletLoading = true;
    },
    fetchParticipantWalletSuccess: (state, action) => {
      state.participantWalletLoading = false;
      state.participantWallet.wallets = action.payload?.list ?? [];
      state.participantWallet.total = action.payload?.total ?? 0;
    },
    fetchParticipantWalletFail: (state, action) => {
      state.participantWalletLoading = false;
      state.participantWallet.wallets = [];
      state.participantWallet.total = 0;
      state.error = action.payload;
    },
    resetParticipantWallet: (state, action) => {
      state.participantWallet.wallets = action.payload.list;
      state.participantWallet.total = action.payload?.total;
    },
    fetchGroupsStart: (state) => {
      state.userGroupsLoading = true;
    },
    fetchGroupsSuccess: (state, action) => {
      state.userGroupsLoading = false;
      state.userGroups.list = action.payload?.list ?? [];
      state.userGroups.total = action.payload?.total ?? 0;
    },
    fetchGroupsFail: (state, action) => {
      state.userGroupsLoading = false;
      state.userGroups.list = [];
      state.userGroups.total = 0;
      state.error = action.payload;
    },
    fetchGroupListStart: (state) => {
      state.groupsLoading = true;
    },
    fetchGroupListSuccess: (state, action) => {
      state.groupsLoading = false;
      state.groups.list = action.payload?.list ?? [];
      state.groups.total = action.payload?.total ?? 0;
    },
    fetchGroupListFail: (state, action) => {
      state.groupsLoading = false;
      state.groups.list = [];
      state.groups.total = 0;
      state.error = action.payload;
    },
    resetUserDetail: (state) => {
      state.userDetail = initialUserDetail;
      state.WalletConnect = { wallets: [], total: 0 };
      state.walletList = { list: [], total: 0 };
      state.transList = { trans: [], total: 0 };
      state.walletTransaction = [];
      state.cards = { list: [], total: 0 };
      state.participants = { list: [], total: 0 };
      state.participantWallet = { wallets: [], total: 0 };
      state.userGroups = { list: [], total: 0 };
      state.groups = { list: [], total: 0 };
      state.guardianDetail = initialGuardianDetail;
      state.error = null;
    }
  }
});

const {
  fetchUserDetailStart,
  fetchUserDetailSuccess,
  fetchUserDetailFail,
  fetchWalletsStart,
  fetchWalletsSuccess,
  fetchWalletsFail,
  fetchWalletListStart,
  fetchWalletListSuccess,
  fetchWalletListFail,
  updateUserDetailStart,
  updateUserDetailSuccess,
  updateUserDetailFail,
  updateUserRoleStart,
  updateUserRoleSuccess,
  updateUserRoleFail,
  updateUserNdisStart,
  updateUserNdisSuccess,
  updateUserNdisFail,
  addSupporterStart,
  addSupporterSuccess,
  addSupporterFail,
  deleteSupporterStart,
  deleteSupporterSuccess,
  deleteSupporterFail,
  fetchTransListStart,
  fetchTransListSuccess,
  fetchTransListFail,
  resetPasswordStart,
  resetPasswordFail,
  resetPasswordSuccess,
  getAllCardsStart,
  getAllCardsSuccess,
  getAllCardsFail,
  fetchParticpantStart,
  fetchParticpantSuccess,
  fetchParticpantFail,
  fetchParticipantWalletStart,
  fetchParticipantWalletSuccess,
  fetchParticipantWalletFail,
  fetchGroupsFail,
  fetchGroupsStart,
  fetchGroupsSuccess,
  fetchGroupListFail,
  fetchGroupListStart,
  fetchGroupListSuccess
} = userDetailSlice.actions;

export const { resetParticipantWallet, resetUserDetail, setUserMfaExempt } = userDetailSlice.actions;

export const fetchUserDetailAction = (data: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchUserDetailStart());
    try {
      const response = await getUserById({ user_id: data });
      const user = response.data.get_user;

      dispatch(
        fetchUserDetailSuccess({
          user: {
            id: user.user_id,
            cognitoUserName: user.cognito_username,
            initial: user.attributes.initial,
            firstName: user.attributes.first_name,
            lastName: user.attributes.last_name,
            email: user.email,
            userName: user.username,
            gender: user.attributes.gender,
            dob: user.attributes.date_of_birth,
            role: user.roles,
            phoneNumber: user.phone,
            line1: user.attributes.address.line_1,
            line2: user.attributes.address.line_2,
            city: user.attributes.address.city,
            state: user.attributes.address.state,
            zipcode: user.attributes.address.post_code,
            country: user.attributes.address.country,
            active: user.is_active,
            kycStatus: user.security_status,
            guardianId: user.guardian_id,
            profileImage: user.attributes.profile_image,
            displayId: user.display_id,
            preferences: {
              ndis_number: user?.preferences?.ndis_number,
              licence_tag: user?.preferences?.licence_tag,
              exclude_license: user?.preferences?.exclude_license,
              mfa_exempt: user?.preferences?.mfa_exempt === true
            }
          }
        })
      );
    } catch (error) {
      dispatch(fetchUserDetailFail(error));
    }
  };
};

export const fetchWalletConnectAction = (data: { userId: string }) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchWalletsStart());
    try {
      if (data.userId != undefined) {
        const response = await getConnectedUserWallets(data.userId);

        const structuredResponse = response.data.get_connected_user_wallets.map((wallet: any) => ({
          id: wallet.wallet_id,
          displayId: wallet.display_id,
          description: wallet.description,
          name: wallet.name,
          walletType: wallet.wallet_type,
          participant: {
            // eslint-disable-next-line no-nested-ternary
            id: wallet?.user ? wallet?.user?.user_id : wallet?.group ? wallet?.group?.group_id : EMPTY_STRING,
            // eslint-disable-next-line no-nested-ternary
            fullName: wallet?.user
              ? formatFullName(wallet?.user?.attributes?.first_name, wallet?.user?.attributes?.last_name)
              : wallet?.group
                ? wallet?.group?.name
                : EMPTY_STRING
          },
          balance: wallet.balance
        }));

        dispatch(
          fetchWalletsSuccess({
            list: structuredResponse,
            total: structuredResponse.length
          })
        );
      }
    } catch (error) {
      dispatch(fetchWalletsFail(error));
    }
  };
};

export const fetchWalletListAction = (data: SearchWalletRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchWalletListStart());
    try {
      const response = await searchWallet(data);

      const structuredResponse = response.data.search_wallet.items.map((wallet: any) => ({
        id: wallet.wallet_id,
        displayId: wallet.display_id,
        description: wallet.description,
        balance: wallet.balance,
        walletType: wallet.wallet_type,
        name: wallet.name,
        participant: {
          role: "PARTICIPANT",
          fullName: formatFullName(wallet.user?.attributes?.first_name, wallet.user?.attributes?.last_name),
          email: wallet?.user?.email
        }
      }));

      dispatch(
        fetchWalletListSuccess({
          list: structuredResponse,
          total: response.data.search_wallet.total_size
        })
      );
    } catch (error) {
      dispatch(fetchWalletListFail(error));
    }
  };
};

export const fetchTransListAction = (data: TransactionRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchTransListStart());
    try {
      const response = await getAllTransaction(data);
      const structuredResponse = response.data.search_activity.activity_listing.map((data: any) => ({
        id: data.activity_id,
        name: data.activity_name,
        endDate: data.end_timestamp,
        startDate: data.start_timestamp,
        ownerId: data.owner_id,
        activityStatus: data.activity_status,
        approval_number: data.approval_number,
        data: JSON.parse(data?.data)
      }));

      dispatch(
        fetchTransListSuccess({
          list: structuredResponse,
          total: response.data.search_activity.total_size
        })
      );
      return response;
    } catch (err) {
      dispatch(fetchTransListFail(err));
    }
  };
};

export const fetchWalletTransactionAction = (data: any) => {
  return data;
};

export const updateUserDetailAction = (data: UpdateUserRequest, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(updateUserDetailStart());
    try {
      await updateUser(data);

      dispatch(updateUserDetailSuccess());
      if (cb) cb();

      swal.fire({
        position: "center",
        icon: "success",
        title: "User info updated Sucessfully",
        showConfirmButton: false,
        timer: 2500
      });
    } catch (error) {
      dispatch(updateUserDetailFail(error));
    }
  };
};

export const updateUserRoleAction = (data: UpdateUserRoleRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(updateUserRoleStart());
    try {
      const response = await updateUserRole(data);

      dispatch(updateUserRoleSuccess());
      return response;
    } catch (error) {
      dispatch(updateUserRoleFail(error));
    }
  };
};

export const saveUserPreferenceAction = (data: SaveUserPreferencesInput) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(updateUserNdisStart());
    try {
      await saveUserPreferences(data);

      dispatch(updateUserNdisSuccess());
    } catch (error) {
      dispatch(updateUserNdisFail(error));
    }
  };
};

// Errors are toasted by the API client.
export const saveUserMfaExemptAction = (userId: string, mfaExempt: boolean, cb?: () => void, fcb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    try {
      const response = await getUserPreferencesForSave(userId);

      await saveUserMfaExemption(userId, response.data?.get_user?.preferences ?? null, mfaExempt);
      dispatch(setUserMfaExempt(mfaExempt));
      cb?.();
    } catch {
      fcb?.();
    }
  };
};

export const resetUserMfaAction = (userId: string, cb?: () => void, fcb?: () => void) => {
  return async () => {
    try {
      await resetUserMfa(userId);
      cb?.();
    } catch {
      fcb?.();
    }
  };
};

export const addWalletSupporterAction = (walletId: string, userId: string, toAll: boolean = false, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(addSupporterStart());
    try {
      await connectUserToWallet(walletId, userId, toAll);

      dispatch(addSupporterSuccess());
      immediateToast("success", {
        message: "User connected successfully.",
        timeout: 3000,
        position: "topCenter"
      });
      if (cb) cb();
    } catch (error) {
      dispatch(addSupporterFail(error));
    }
  };
};

export const deleteWalletSupporterAction = (walletId: string, userId: string, removeFromAll: boolean) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(deleteSupporterStart());
    try {
      await removeUserFromWallet(walletId, userId, removeFromAll);

      dispatch(deleteSupporterSuccess());
      immediateToast("success", {
        message: "User Removed Successfully",
        timeout: 3000,
        position: "topCenter"
      });
    } catch (error) {
      dispatch(deleteSupporterFail(error));
    }
  };
};

export const resetPasswordAction = (userId: string, cognitoUserName: string | null | undefined, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(resetPasswordStart());
    try {
      const response = await resetPassword(userId, cognitoUserName ?? null);

      if (response.data.update_user_password.success) {
        dispatch(resetPasswordSuccess());
        if (cb) cb();

        swal.fire({
          position: "center",
          icon: "success",
          title: "Password reset Successfully",
          html: `
            <div style="margin-top: 20px;">
              <p style="margin-bottom: 20px;">Your new password is: <strong>${response.data.update_user_password.password}</strong> 
                <span id="downloadIcon" style="margin-left: 8px; cursor: pointer; color: #0f172a; font-size: 18px; display: inline-flex; align-items: center; padding: 4px; border: 1px solid #0f172a; border-radius: 4px;" title="Download password">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                  </svg>
                </span>
              </p>
              <div style="display: flex; gap: 10px; justify-content: center;">
                <button id="copyBtn" class="swal2-confirm swal2-styled" style="background-color: #0f172a;">Copy</button>
                <button id="closeBtn" class="swal2-cancel swal2-styled">Close</button>
              </div>
            </div>
          `,
          showConfirmButton: false,
          showCancelButton: false,
          showDenyButton: false,
          allowOutsideClick: false,
          allowEscapeKey: false,
          didOpen: () => {
            document.getElementById("copyBtn")?.addEventListener("click", () => {
              navigator.clipboard.writeText(response.data.update_user_password.password);
              immediateToast("success", {
                message: "Password copied to clipboard",
                timeout: 3000,
                position: "topCenter"
              });
            });

            document.getElementById("downloadIcon")?.addEventListener("click", () => {
              const passwordData = `New Password: ${response.data.update_user_password.password}\nGenerated on: ${new Date().toLocaleString()}`;
              const blob = new Blob([passwordData], { type: "text/plain" });
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement("a");

              link.href = url;
              link.download = `password_${userId}_${new Date().getTime()}.txt`;

              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
              immediateToast("success", {
                message: "Password downloaded successfully",
                timeout: 3000,
                position: "topCenter"
              });
            });

            document.getElementById("closeBtn")?.addEventListener("click", () => {
              swal.close();
            });
          }
        });
      } else {
        dispatch(resetPasswordFail("Password reset failed"));
        immediateToast("error", {
          message: "Password reset failed",
          timeout: 3000,
          position: "topCenter"
        });
      }
    } catch (error) {
      dispatch(resetPasswordFail(error));
      immediateToast("error", {
        message: "Password reset failed",
        timeout: 3000,
        position: "topCenter"
      });
    }
  };
};

export const getAllCardsAction = (data: CardSearchRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(getAllCardsStart());
    try {
      const response = await cardSearch(data);
      const structuredResponse = response.data.search_card.card_listing?.map(
        (card: any) =>
          ({
            id: card.card_id,
            bsb: card.bsb,
            cardStatus: card.card_status,
            accessType: card.card_type,
            cardNumber: card.ppan,
            orderId: card.order_id,
            clientId: card.clientId,
            cardLimit: card.card_limit,
            cardHolderName: card.holder_name,
            created_at: card.created_at,
            first_name: card.first_name,
            last_name: card.last_name,
            object_id: card.object_id,
            object_type: card.object_type,
            address: card.address,
            user: {
              id: card?.user_id,
              firstName: card?.user?.attributes?.first_name ?? "---",
              lastName: card?.user?.attributes?.last_name ?? " ",
              email: card?.user?.email ?? " "
            }
          }) as Card
      );

      dispatch(
        getAllCardsSuccess({
          list: structuredResponse,
          total: response.data.search_card.total_size
        })
      );
    } catch (error) {
      dispatch(getAllCardsFail(error));
    }
  };
};

export const fetchParticpantAction = (data: SearchUserRequest, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchParticpantStart());
    try {
      const response = await searchUser(data);

      const structuredResponse = response.data.search_user.user_listing.map((user: any) => ({
        id: user?.display_id,
        firstName: user?.attributes?.first_name,
        lastName: user?.attributes?.last_name,
        email: user?.email,
        currentOrganisationRole: user?.current_organisation_role,
        status: user?.is_active,
        userId: user?.user_id,
        createdAt: user?.created_at
      }));

      dispatch(
        fetchParticpantSuccess({
          list: structuredResponse,
          total: response.data.search_user.total_size
        })
      );
      if (cb) cb();
    } catch (error) {
      dispatch(fetchParticpantFail(error));
    }
  };
};

export const fetchParticipantWalletAction = (userId: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchParticipantWalletStart());
    try {
      const response = await getConnectedUserWallets(userId);

      const structuredResponse = response.data.get_connected_user_wallets.map((wallet: any) => ({
        id: wallet.wallet_id,
        displayId: wallet.display_id,
        description: wallet.description,
        balance: wallet.balance,
        // status: wallet.wallet_status,
        walletType: wallet.wallet_type,
        name: wallet.name,
        participant: {
          role: "PARTICIPANT",
          email: wallet?.user?.email,
          fullName: formatFullName(wallet?.user?.attributes?.first_name, wallet?.user?.attributes?.last_name)
        }
      }));

      dispatch(
        fetchParticipantWalletSuccess({
          list: structuredResponse,
          total: structuredResponse.length
        })
      );
    } catch (error) {
      dispatch(fetchParticipantWalletFail(error));
    }
  };
};

export const fetchUserGroupAction = (id: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchGroupsStart());
    try {
      const response = await getUserGroup(id);

      const structuredResponse = response.data.get_user_groups;

      dispatch(
        fetchGroupsSuccess({
          list: structuredResponse,
          total: response.data.get_user_groups.length
        })
      );
    } catch (error) {
      dispatch(fetchGroupsFail(error));
    }
  };
};

export const fetchGroupListAction = (data: SearchGroupListRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchGroupListStart());
    try {
      const response = await getGroupList(data);

      const structuredResponse = response.data.search_group?.items;

      dispatch(
        fetchGroupListSuccess({
          list: structuredResponse,
          total: response.data.search_group.total_size
        })
      );
    } catch (error) {
      dispatch(fetchGroupListFail(error));
    }
  };
};

export default userDetailSlice.reducer;
